import { useState } from 'react';

import { Box, Button, ButtonGroup, FormHelp, Legend, Text, TextButton } from '@zillow/constellation-10';

import { type PhoneVerificationSource, PhoneVerificationType } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUserPhoneVerificationV2WebApiHandler_sendVerificationCode } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import numberUtils from '../../../../utils/numberUtils';

import StringConstants from '../../../../constants/StringConstants';
import { VERIFICATION_STEPS } from '../constants';

import MaxAttemptsError from './MaxAttemptsError';
import VerificationBodyTemplate from './VerificationBodyTemplate';

const CONFIG = {
  value: (val: string) => numberUtils.formatPhoneNumberInput(val),
  editButtonText: 'Edit number',
};

type Props = {
  contentValue: string;
  currentStepIndex: number;
  incrementStep: (step?: string) => void;
  isContentEditable: boolean;
  saveVerificationType: (type: PhoneVerificationType) => void;
  verificationSource: PhoneVerificationSource;
};

type ErrorState = { code?: string; message?: string };

const GetVerificationCode = ({
  contentValue,
  currentStepIndex,
  incrementStep,
  isContentEditable,
  saveVerificationType,
  verificationSource,
}: Props) => {
  const { mutateAsync: sendVerificationCode } =
    useRentalManagerUserPhoneVerificationV2WebApiHandler_sendVerificationCode({
      meta: {
        context: {
          api: 'rental-manager',
          details: 'Sending phone verification code from verification modal',
        },
      },
    });

  const [error, setError] = useState<ErrorState>({});

  const isButtonDisabled = error?.code === StringConstants.SS_ERRORS.attempt_limit_reached;

  const handleRequestPhoneVerification = (type: PhoneVerificationType) => {
    sendVerificationCode({
      postData: {
        phone: contentValue,
        verificationType: type,
        verificationSource,
      },
    })
      .then(() => {
        saveVerificationType(type);
        return incrementStep();
      })
      .catch((err: { response?: { data?: { message?: string; code?: string } }; message?: string }) => {
        const message = err?.response?.data?.message ?? err?.message ?? '';
        const code = err?.response?.data?.code;
        if (message?.includes('Contact already verified')) {
          incrementStep(VERIFICATION_STEPS.VERIFICATION_SUCCESS);
        } else {
          setError({ code, message });
        }
      });
  };

  const requestVerificationCode = (type: PhoneVerificationType) => {
    handleRequestPhoneVerification(type);
  };

  const renderErrorMessage = () => {
    if (isButtonDisabled) {
      return <MaxAttemptsError verificationMode="phone" />;
    }
    return (
      <FormHelp error={true} css={{ marginTop: '400', fontSize: 'body-sm' }}>
        {error.message}
      </FormHelp>
    );
  };

  return (
    <VerificationBodyTemplate
      heading={`Step ${currentStepIndex + 1} - Get verification code`}
      subheading={
        <Box css={{ marginBottom: { base: '200', lg: '600' } }}>
          <Text asChild>
            <p>We&#39;ll send a one-time verification code to</p>
          </Text>
          <Text asChild>
            <p className="fs-mask">
              {`${CONFIG.value(contentValue)}. `}
              {isContentEditable && (
                <TextButton
                  tone="brand"
                  onClick={() => incrementStep(VERIFICATION_STEPS.ADD_CONTENT)}
                  css={{
                    fontSize: 'body-sm',
                    fontWeight: 400,
                    textDecoration: 'underline',
                    xl: { fontSize: 'body' },
                  }}
                >
                  {CONFIG.editButtonText}
                </TextButton>
              )}
            </p>
          </Text>
        </Box>
      }
    >
      <Legend
        css={{
          textStyle: { base: 'body-sm-bold', lg: 'body-bold' },
          display: 'block',
          marginBottom: { base: '200', lg: '400' },
        }}
      >
        How would you like to receive the code?
      </Legend>
      <ButtonGroup aria-label="How would you like to receive the code?" css={{ margin: '{spacing.400} auto 0' }}>
        <Button
          tone="brand"
          emphasis="filled"
          disabled={isButtonDisabled}
          onClick={() => requestVerificationCode(PhoneVerificationType.SMS)}
          css={{ minWidth: '200px', display: 'block', margin: '0 auto' }}
        >
          Send text message
        </Button>
        <Box css={{ marginTop: '400' }} />
        <Button
          tone="brand"
          emphasis="outlined"
          disabled={isButtonDisabled}
          onClick={() => requestVerificationCode(PhoneVerificationType.PHONE)}
          css={{ minWidth: '200px', display: 'block', margin: '0 auto' }}
        >
          Call me
        </Button>
      </ButtonGroup>
      {Boolean(error.message) && <Box css={{ marginBlock: '200' }}>{renderErrorMessage()}</Box>}
    </VerificationBodyTemplate>
  );
};

export default GetVerificationCode;
